<template>
  <div class="dashboard-block">
    <ladda-button
      v-if="canDeleteUser"
      key="delete"
      type="button"
      class="btn btn-sm btn-danger"
      :click="handleDeleteUserCallback"
      :disabled="userIsAdmin && !canEditAdmin">
      <i class="fa fa-trash"></i> Delete Account
    </ladda-button>

    <template v-if="canPerformAdministrativeActions">
      <ladda-button
        v-if="userIsDeleted"
        key="restore"
        class="btn btn-sm btn-info"
        :click="handleRestoreUserCallback"
        :done="handleRestoreUserDoneCallback">
        <i class="fa fa-refresh"></i> Restore {{ userDisplayRole }}
      </ladda-button>

      <ladda-button
        v-if="canPermanentlyDeleteUser"
        key="permanentlyDelete"
        class="btn btn-sm btn-danger"
        :click="handlePermanentlyDeleteUserCallback">
        <i class="fa fa-trash"></i> Permanently Delete {{ userDisplayRole }}
      </ladda-button>

      <ladda-button
        v-if="canVerifyUser"
        key="verify"
        class="btn btn-sm btn-success"
        :click="handleVerifyUserCallback"
        :disabled="userIsAdmin && !canEditAdmin">
        <i class="fa fa-check"></i> Verify {{ userDisplayRole }} Email
      </ladda-button>

      <ladda-button
        v-if="canActivateUser"
        key="activate"
        class="btn btn-sm btn-success"
        :click="handleActivateUserCallback">
        <i class="fa fa-check"></i> Activate {{ userDisplayRole }}
      </ladda-button>

      <ladda-button
        v-if="canActivateUser && $vlastMode"
        key="resendActivationEmail"
        class="btn btn-sm btn-success"
        :click="handleResendActivationEmail">
        <i class="fa fa-check"></i> Send Activation Email
      </ladda-button>

      <ladda-button
        v-if="canActivateUser && $vlastMode"
        key="handleSendActivationSMS"
        class="btn btn-sm btn-success"
        :click="handleSendActivationSMS">
        <i class="fa fa-check"></i> Send SMS Activation
      </ladda-button>

      <ladda-button
        v-if="canDeactivateUser"
        key="deactivate"
        class="btn btn-sm btn-danger"
        :click="handleDeactivateUserCallback">
        <i class="fa fa-ban"></i> Deactivate {{ userDisplayRole }}
      </ladda-button>
      <a v-if="!$vlastMode && $userIsClient" class="btn btn-sm btn-info" :href="createNewRequestLink"><i class="fa fa-plus"></i> Create New Request</a>
    </template>
    <a v-if="!$vlastMode && user.role === 'talent' && user.status === 'active' && user.profile && user.profile.visibility === 'public'" class="btn btn-sm btn-info" :href="viewPublicProfileLink" target="_blank"><i class="fa fa-arrow-circle-right"></i> View Public Profile</a>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { helpers } from 'mixins/users'
import { alert, notify, publicUrl, http } from 'utils'
import { OpportunityTracker } from 'mixins/analytics'

export default {
  mixins: [helpers, OpportunityTracker],

  computed: {
    ...mapState('user', ['user']),

    canPerformAdministrativeActions () {
      if (this.$userIsAdmin && this.$user.id !== this.user.id) {
        return true
      }

      if (this.$userIsManager && this.$user.id === this.user.parent_id) {
        return true
      }

      return false
    },

    canActivateUser () {
      return this.userIsNotActivated
    },

    canDeactivateUser () {
      if (!this.userIsActivated) {
        return false
      }
      return this.userIsStaffManager || (this.userIsTalent && this.$vlastMode)
    },

    canVerifyUser () {
      return this.$userIsAdmin && this.userIsUnverified
    },

    canDeleteUser () {
      if (this.$user.id === this.user.id) {
        if (this.$userIsAdmin) {
          return false
        }

        return this.userNotDeleted
      }

      if (this.$userIsAdmin && this.userNotDeleted) {
        return true
      }

      if (this.$userIsManager && this.$user.id === this.user.parent_id && this.userNotDeleted) {
        return true
      }

      return false
    },

    canPermanentlyDeleteUser () {
      return this.$userIsAdmin && this.userIsDeleted
    },

    createNewRequestLink () {
      return publicUrl(`our-stars/request?client=${this.user.id}`)
    },

    viewPublicProfileLink () {
      return publicUrl(`our-stars/${this.user.id}/${this.user.slug}`)
    }
  },

  methods: {
    ...mapActions('user', ['verifyUser', 'deleteUser', 'restoreUser', 'activateUser', 'deactivateUser', 'permanentlyDeleteUser', 'setUserProperty', 'requestDeletion']),

    handleVerifyUserCallback () {
      return this.verifyUser({ id: this.user.id })
        .then((response) => {
          notify.success('User successfully verified.')
        })
    },

    handleActivateUserCallback () {
      return this.activateUser({ id: this.user.id })
        .then((response) => {
          notify.success('User successfully activated.')
        })
    },

    handleResendActivationEmail () {
      return http.post(`user_activation/${this.user.id}/notify_single`).then((response) => {
        notify.success('Notifications have been sent successfully.')
      }).catch((error) => {
        notify.error('Error occurred.')
        return Promise.reject(error)
      })
    },

    handleSendActivationSMS () {
      return http.post(`user_activation/${this.user.id}/notify_sms`).then((response) => {
        notify.success('Notifications have been sent successfully.')
      }).catch((error) => {
        notify.error('Error occurred.')
        return Promise.reject(error)
      })
    },

    handleDeactivateUserCallback () {
      return this.deactivateUser(this.user.id)
        .then((response) => {
          notify.success('User successfully deactivated.')
        })
    },

    handleDeleteUserCallback () {
      this.trackEvent(this.analytics.V2.AccountDeleteAccountClickEvent)
      if (this.$userIsAdmin === false) {
        alert.info({
          title: 'Delete Account',
          text: `Your deletion request has been sent to the ${this.clientName} team. We will be in contact to confirm this deletion.`
        })
        return this.requestDeletion({id: this.user.id}).catch(error => {
          if (error.response.status === 400) {
            notify.error(error.response.data.message)
          } else {
            notify.error('Sorry something went wrong, please refresh the page and try again.')
          }
        })
      } else {
        return alert.confirm({
          title: 'Delete User Account',
          text: 'Are you sure you want to continue and delete the account?',
          buttons: {
            confirm: {
              text: 'Yes, delete account.'
            }
          }
        }).then(() => {
          return this.deleteUser({id: this.user.id})
            .then(response => {
              this.setUserProperty({ property: 'deleted_at', value: response.data.deleted_at })

              if (this.user.id === this.$user.id) {
                this.$router.push({ name: 'logout' })
              }
            })
            .catch(error => {
              if (error.response.status === 400) {
                notify.error(error.response.data.message)
              } else {
                notify.error('Sorry something went wrong, please refresh the page and try again.')
              }
            })
        })
      }
    },

    handlePermanentlyDeleteUserCallback () {
      return alert.confirm({
        title: 'Permanently Delete User Account',
        text: 'The account and all associated data will no longer be recoverable. Are you sure you want to delete this account?',
        buttons: {
          confirm: {
            text: 'Yes, delete account.'
          }
        }
      }).then(() => {
        return this.permanentlyDeleteUser({ id: this.user.id })
          .then(() => {
            notify.success('User account successfully deleted.')

            if (this.user.role === 'talent') {
              this.$router.push({ name: 'talent.index' })
            } else if (this.user.role === 'client') {
              this.$router.push({ name: 'clients.index' })
            } else if (this.user.role === 'manager') {
              if (this.user.parent_id) {
                this.$router.push({ name: 'managers.profile.show', params: { id: this.user.parent_id } })
              } else {
                this.$router.push({ name: 'managers.index' })
              }
            }
          })
          .catch(error => {
            if (error.response.status === 400) {
              notify.error(error.response.data.message)
            } else {
              notify.error('Sorry something went wrong, please refresh the page and try again.')
            }
          })
      })
    },

    handleRestoreUserCallback () {
      return this.restoreUser({ id: this.user.id })
    },

    handleRestoreUserDoneCallback (response) {
      this.setUserProperty({ property: 'deleted_at', value: response.data.deleted_at })
    }
  }
}
</script>
